import React, { useCallback, useMemo, useRef, useState } from 'react';
import { AutomationPromoBanner } from '../AutomationPromoBanner/AutomationPromoBanner';
import ScreeningStudyListItem from '../StudyListItem/ScreeningStudyListItem';
import {
  Study,
  DeleteTagFunction,
  FetchNextPageFunction,
  UndoDuplicateFunction,
  MarkAsDuplicateFunction,
} from '../StudyList';
import { StudyListProvider } from '../StudyListItem/StudyListProvider';
import {
  StudyListToolbar,
  ToolbarOptions,
} from '../StudyListToolbar/StudyListToolbar';
import StudyListWithEmptyStateManagement from '../StudyListWithEmptyStateManagement';
import {
  EmptyListNoImports,
  EmptyListTrialLimit,
  EmptyListTrialLimitProps,
  EmptyListType,
} from '../EmptyList/StudyListEmptyState';
import { StudyListLayoutProvider } from '../StudyListLayoutContext';
import { LibkeyProvider } from '../components/LibkeyProvider';
import { useRecentlyAdded } from '../useRecentlyAdded';
import { useDuplicateManagement } from '../useDuplicateManagement';
import styles from './ScreeningStudyListPage.module.scss';
import { useScreeningVotes } from './useScreeningVotes';
import { ReviewFeaturesProvider } from 'contexts/ReviewFeaturesProvider';
import { Text } from 'components/core';
import { PageContainer, PrimaryNavigationLayout } from 'components/shared';
import type { PrimaryNavigationLayoutProps } from 'components/shared/PrimaryNavigation/PrimaryNavigationLayout';

// The screening list mounts LibkeyProvider purely so DoiLink can suppress the
// LibKey Nomad extension hook (`ref-ids`) for reviewers who have selected
// their own LibKey library.
const NO_REFERENCE_IDS: never[] = [];

// Placeholders until we start implementing certain functions
const onDeleteTag: DeleteTagFunction = () => undefined;
const fetchNextPage: FetchNextPageFunction = () => Promise.resolve();

const selectedStudyIds: Study['id'][] = [];
const noop = () => undefined;
// Matches the Backbone fetch response the toolbar reads total_entries from
const sortAndFilter = () => Promise.resolve([{ total_entries: 0 }]);

const toolbarOptions: ToolbarOptions = {
  isCriteriaEnabled: true,
  isHighlightsSidebarEnabled: true,
  isMergeStudiesEnabled: false,
  isDiscussionsEnabled: false,
  reviewStage: 'screening',
};

type TrialStats = EmptyListTrialLimitProps & { trialLimitReached: boolean };

type ScreeningStudyListPageProps = PrimaryNavigationLayoutProps & {
  reviewId: string;
  showAutomationPromoBanner?: boolean;
  studies: Study[];
  // Only set when the review has no studies at all
  emptyList?: EmptyListType;
  trialStats: TrialStats;
};

export const ScreeningStudyListPage = (props: ScreeningStudyListPageProps) => {
  const {
    reviewId,
    showAutomationPromoBanner,
    studies,
    emptyList,
    trialStats,
    ...layoutProps
  } = props;
  const toolbarRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const subHeaderRef = useRef<HTMLDivElement>(null);
  const pageContentRef = useRef<HTMLDivElement>(null);
  // The sidebars portal into this element during render, so store it in state to rerender once it's attached
  // When Backbone is removed from study list pages, we could look at lifting sidebar state out of the toolbar and into the parent React component, then remove the ref
  const [
    sidebarPanelRoot,
    setSidebarPanelRoot,
  ] = useState<HTMLDivElement | null>(null);
  const sidebarPanelRef = useCallback((node: HTMLDivElement | null) => {
    setSidebarPanelRoot(node);
  }, []);

  const { wasRecentlyAdded, withRecentlyAddedTracking } = useRecentlyAdded();
  const {
    undoDuplicate,
    markAsDuplicate,
    undoneStudyIds,
  } = useDuplicateManagement();

  const {
    handleScreeningVote,
    votedStudies,
    isTrialLimitReached,
  } = useScreeningVotes({
    reviewId,
    initialIsTrialLimitReached: trialStats.trialLimitReached,
  });

  // Hide studies instead of removing them, so undo can show them again in place
  const hiddenStudyIds = useMemo(() => {
    const votedIds = new Set<number>(
      votedStudies.map((votedStudy) => votedStudy.studyId)
    );

    return votedIds.union(undoneStudyIds);
  }, [votedStudies, undoneStudyIds]);

  const visibleStudies = useMemo(
    () => studies.filter((study) => !hiddenStudyIds.has(study.id)),
    [studies, hiddenStudyIds]
  );

  const lastVotedStudy = votedStudies[votedStudies.length - 1];
  const lastVotedStudyTitle = studies.find(
    (study) => study.id === lastVotedStudy?.studyId
  )?.title;

  const undoDuplicateWithTracking = useMemo(
    () => withRecentlyAddedTracking(undoDuplicate),
    [withRecentlyAddedTracking, undoDuplicate]
  );
  const handleVote = useMemo(
    () => withRecentlyAddedTracking(handleScreeningVote),
    [withRecentlyAddedTracking, handleScreeningVote]
  );

  const handleMarkAsDuplicate: MarkAsDuplicateFunction = useCallback(
    (studyId, isError) => {
      const undoneStudy = studies.find((study) => study.id === studyId);

      if (isError || !undoneStudy) {
        return Promise.resolve(studyId);
      }

      markAsDuplicate(undoneStudy);
      return Promise.resolve(studyId);
    },
    [studies]
  );

  const handleUndoDuplicate: UndoDuplicateFunction = useCallback(
    (studyId) => {
      const study = studies.find((i) => i.id === studyId);
      if (!study) return Promise.resolve(studyId);

      return undoDuplicateWithTracking(study.id);
    },
    [studies, undoDuplicateWithTracking]
  );
  const reviewHasStudies = !emptyList;
  const showNoImports = emptyList === 'noImports';

  // Page load counts plus this reviewer's votes, so they miss other reviewers and assume each vote finishes a study (not true for dual screening)
  // TODO: Replace with live counts from the server once pagination loads the list (FALCON-1476)
  const totalScreened = trialStats.totalScreened + votedStudies.length;
  const remainingStudies = trialStats.remainingStudies - votedStudies.length;
  const limitReachedByVote = !trialStats.trialLimitReached;

  return (
    <PrimaryNavigationLayout {...layoutProps} subHeaderRef={subHeaderRef}>
      <StudyListLayoutProvider
        toolbarRef={toolbarRef}
        listRef={listRef}
        subHeaderRef={subHeaderRef}
        pageContentRef={pageContentRef}
        sidebarPanelRoot={sidebarPanelRoot}
      >
        <PageContainer hasContentPanel={false}>
          <div ref={pageContentRef}>
            {/* Remove this text later once we are ready */}
            <Text>Study list vertical slice</Text>
            {showAutomationPromoBanner && <AutomationPromoBanner />}
            {showNoImports && (
              <EmptyListNoImports review={{ id: reviewId }} category="screen" />
            )}
            {isTrialLimitReached && (
              <EmptyListTrialLimit
                totalScreened={totalScreened}
                remainingStudies={remainingStudies}
                isReviewOwner={trialStats.isReviewOwner}
                reviewOwnerName={trialStats.reviewOwnerName}
                fullTextReviewPath={trialStats.fullTextReviewPath}
                focusOnMount={limitReachedByVote}
              />
            )}
            {reviewHasStudies && !isTrialLimitReached && (
              <>
                <div ref={toolbarRef} className={styles.ToolbarContainer}>
                  <StudyListToolbar
                    toolbarOptions={toolbarOptions}
                    reviewId={reviewId}
                    handleSelectAll={noop}
                    sortAndFilter={sortAndFilter}
                    onApplyTag={noop}
                    hasCriteria={false}
                    defaultShowAbstracts={false}
                    hasHighlights={false}
                    toggleAbstracts={noop}
                    toggleHighlights={noop}
                    enableHighlights={noop}
                    onHighlightsChange={noop}
                    isAwaitingOtherReviewer={false}
                    previouslyVotedStudyTitle={lastVotedStudyTitle}
                  />
                </div>
                <div ref={listRef} className={styles.List}>
                  <StudyListProvider isHighlightsEnabled={false}>
                    <ReviewFeaturesProvider>
                      <LibkeyProvider
                        reviewId={reviewId}
                        referenceIds={NO_REFERENCE_IDS}
                      >
                        <StudyListWithEmptyStateManagement
                          reviewId={reviewId}
                          category="screen"
                          studies={visibleStudies}
                          pageSize={10}
                          item={(study) => {
                            const isSelected = selectedStudyIds.includes(
                              study.id
                            );

                            return (
                              <ScreeningStudyListItem
                                key={study.id}
                                study={study}
                                reviewId={reviewId}
                                showAbstracts={false}
                                justUndone={wasRecentlyAdded(study.id)}
                                isSelected={isSelected}
                                onDeleteTag={onDeleteTag}
                                onMarkAsDuplicate={handleMarkAsDuplicate}
                                onUndoDuplicate={handleUndoDuplicate}
                                handleScreeningVote={handleVote}
                              />
                            );
                          }}
                          fetchNextPage={fetchNextPage}
                          hasNextPage={false}
                          hasReachedMaxAIResults={true}
                        />
                      </LibkeyProvider>
                    </ReviewFeaturesProvider>
                  </StudyListProvider>
                </div>
              </>
            )}
          </div>
          <div
            ref={sidebarPanelRef}
            // Id kept for existing feature specs, which could move to a test id
            id="studyListSidebarPanel"
            className={styles.SidebarPanel}
          />
        </PageContainer>
      </StudyListLayoutProvider>
    </PrimaryNavigationLayout>
  );
};
